"use client";

import "./klarna.css";

import { Button } from "@/components/atoms";
import VariantSelector from "@/components/atoms/VariantSelector/VariantSelector";
import { convertProductToCartItem } from "@/lib/centra/convertToCartItem/convertToCartItem";
import { DepictViews } from "@/lib/depict/DepictViews";
import { useDPC } from "@/lib/depict/useDPC";
import useLocation from "@/lib/hooks/useLocation";
import useModal from "@/lib/hooks/useModal";
import { useCart } from "@/lib/hooks/useSelection";
import usePriceList from "@/lib/hooks/useSelection/cart/usePriceList";
import useWishlist from "@/lib/hooks/useWishlist";
import {
  eventAddToCart,
  eventProductDetail,
  generateGTagItem,
} from "@/lib/utils/Analytics/Functions";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { Suspense, useEffect, useRef, useState } from "react";
import useSizes from "../../hooks/useSizes";
import type { PDPProduct } from "../../page";
import ComingSoonModal from "../ComingSoonModal/ComingSoonModal";
import SizeSelector from "../SizeSelector/SizeSelector";
import styles from "./action-section.module.css";
export type Sizes = ReturnType<typeof useSizes>;

declare global {
  interface Window {
    Klarna: any;
  }
  namespace JSX {
    interface IntrinsicElements {
      "klarna-placement": any;
    }
  }
}

const ActionSection = ({ product }: { product: PDPProduct }) => {
  const prices = usePriceList(product?.prices);
  const { location, currency } = useLocation();
  const klarnaPrice = prices?.priceAsNumber * 100;
  const router = useRouter();
  const modalState = useState(false);
  const [, setModalOpen] = modalState;
  const ref = useRef<HTMLElement | null>(null);

  const { DPC } = useDPC();

  useEffect(() => {
    if (ref.current && klarnaPrice) {
      ref.current?.setAttribute("data-purchase-amount", klarnaPrice + "");
      ref.current?.setAttribute("data-locale", `en-${location}`);
      if (window.Klarna && window.Klarna.OnsiteMessaging)
        try {
          window.Klarna.OnsiteMessaging.refresh();
        } catch (e) {
          console.error(e);
        }
    }
  }, [location, klarnaPrice, ref]);

  const sizes = useSizes(product?.sizes);

  const itemId = sizes?.selectedSize?.itemId || product?.itemId;

  useEffect(() => {
    if (!currency) return;

    if (eventProductDetail)
      eventProductDetail({
        currency: currency || "",
        items: [generateGTagItem(product, prices?.priceAsNumber)],
        value: prices.priceAsNumber,
      });
  }, [product, currency, prices?.priceAsNumber]);

  const cart = useCart();

  const addToCart = () => {
    if (
      (product?.comingSoon && product?.comingSoon === "1") ||
      !sizes.selectedSize?.stock
    ) {
      setModalOpen(true);
      return;
    }
    if (!sizes?.selectedSize) return false;
    if (sizes?.selectedSize?.itemId) {
      cart.addItem.mutate({
        itemId: sizes?.selectedSize?.itemId,
        cartItem: convertProductToCartItem(
          {
            ...product,
            prices,
          },
          sizes?.selectedSize?.itemId,
        ),
      });
      try {
        eventAddToCart({
          currency: currency || "",
          items: [
            generateGTagItem(
              product,

              prices?.priceAsNumber,
            ),
          ],
          value: prices?.priceAsNumber,
        });
        DPC?.sendAddToCartEvent({
          product_id: sizes?.selectedSize?.itemId.split("-").pop(),
        });
      } catch (e) {
        console.error(e);
      }
    }

    cart.modal.openModal();
    // window.Klarna.OnsiteMessaging.refresh();
  };

  const getButtonLabel = () => {
    if (!sizes?.selectedSize)
      return {
        label: "Select size",
        disabled: true,
      };
    if (
      (product?.comingSoon && product?.comingSoon === "1") ||
      !sizes.selectedSize.stock
    )
      return {
        label: "Notify me when available",
        disabled: false,
      };
    if (
      sizes.sizes &&
      sizes.sizes.length === 1 &&
      sizes.sizes[0].stock === false
    )
      return {
        label: "Out of stock",
        disabled: true,
      };
    if (
      sizes.sizes &&
      sizes.sizes.length > 1 &&
      sizes.sizes.filter(size => size.stock === true).length === 0
    )
      return {
        label: "Out of stock",
        disabled: true,
      };

    if (sizes?.selectedSize?.fewLeft)
      return {
        label: "Add to cart",
        disabled: false,
      };

    if (
      sizes.sizes &&
      sizes.sizes.length === 1 &&
      sizes.sizes[0].stock === true
    )
      return {
        label: "Add to cart",
        disabled: false,
      };

    return {
      label: "Add to cart",
      disabled: false,
    };
  };

  const { toggle, isOnWishlist } = useWishlist();
  const modal = useModal("wishlist", () => {}, true);

  const buttonLabel = getButtonLabel();
  return (
    <div>
      <VariantSelector product={product} />
      <SizeSelector
        sizes={sizes}
        comingSoon={Boolean(product?.comingSoon && product?.comingSoon === "1")}
        product={product}
        showSizeGuide={true}
        onNotifyClick={() => setModalOpen(true)}
      />
      <ComingSoonModal
        item={sizes.selectedSize}
        state={modalState}
        product={product}
      />

      <div className={styles["product-actions"]}>
        {/* <Button
          outlined
          color="transparent"
          className="uppercase"
          onClick={() => {
            if (!itemId) return;
            toggle(itemId)();
            if (!isOnWishlist(itemId)) modal.openModal();
          }}
        >
          {!isOnWishlist(itemId) ? "Add to wishlist" : "Remove from wishlist"}
        </Button>
 */}
        <Button
          color="black"
          textColor="white"
          onClick={addToCart}
          disabled={buttonLabel.disabled}
          grow
          className={clsx("uppercase", styles.cart__button)}
        >
          {buttonLabel.label}
        </Button>
        <DepictViews.Size selectedSize={sizes.selectedSize} />
      </div>
      <Suspense fallback={<div>Loading...</div>}>
        {klarnaPrice && location && (
          <klarna-placement
            ref={ref}
            data-key="credit-promotion-auto-size"
            data-locale={`en-${location}`}
            data-purchase-amount={klarnaPrice}
          ></klarna-placement>
        )}
      </Suspense>
    </div>
  );
};

export default ActionSection;
